import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LockClosedIcon, UserIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import i18n from '../i18n';

export default function Login() {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [error, setError] = useState(null);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => setCredentials({ ...credentials, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            const response = await axios.post('/api/v1/auth/login', credentials);
            const { email, role, full_name } = response.data;
            login(email, role);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Invalid credentials or unauthorized account.');
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center animate-fade-in px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <div className="w-20 h-20 bg-emerald-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 shrink-0 border border-emerald-500/20">
                        <LockClosedIcon className="w-10 h-10 text-emerald-500" />
                    </div>
                    <h1 className="text-3xl font-black text-white tracking-widest uppercase">{i18n.t('auth_title')}</h1>
                    <p className="text-gray-500 font-medium mt-2">{i18n.t('auth_subtitle')}</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 flex items-center gap-3 animate-shake">
                        <ShieldExclamationIcon className="w-6 h-6 shrink-0" />
                        <p className="text-xs font-bold uppercase">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="glass-card p-8 rounded-[32px] space-y-6">
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <UserIcon className="w-3.5 h-3.5 text-[#155e75]" />
                                {i18n.t('email_label')}
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    value={credentials.email}
                                    onChange={handleChange}
                                    className="full-input"
                                    placeholder="your email"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <LockClosedIcon className="w-3.5 h-3.5 text-[#155e75]" />
                                {i18n.t('pass_label')}
                            </label>
                            <div className="relative">
                                <input
                                    type="password"
                                    name="password"
                                    required
                                    value={credentials.password}
                                    onChange={handleChange}
                                    className="full-input font-mono"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black py-4 rounded-2xl shadow-xl shadow-emerald-500/20 transition-all hover:-translate-y-1 mt-6 tracking-widest uppercase text-sm"
                    >
                        {i18n.t('btn_auth')}
                    </button>

                    <p className="text-[10px] text-gray-600 text-center uppercase tracking-widest font-bold mt-6 leading-relaxed">
                        Authorized personnel only. Access is cryptographically logged to the central Hub.
                    </p>
                </form>
            </div>
        </div>
    );
}
